import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import { ThemeProvider } from "next-themes";
import App from "@/App";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

// Validate browser-owned storage before the theme provider reads its initial value.
try {
  const savedTheme = localStorage.getItem("cg_theme");
  if (savedTheme !== null && savedTheme !== "light" && savedTheme !== "dark") {
    localStorage.removeItem("cg_theme");
  }
} catch {}

document.title = "ClippedUp | AI Video Clips";
const publicSite = new URL("/", process.env.REACT_APP_BACKEND_URL).href;
const shareImage = new URL("clippedup-share.png?v=2", publicSite).href;
const shareDescription = "Your videos, clipped up. Turn long videos into ready-to-post clips with AI highlights, vertical framing and editable captions.";
for (const [attribute, name, content] of [
  ["name", "description", shareDescription],
  ["property", "og:site_name", "ClippedUp"],
  ["property", "og:type", "website"],
  ["property", "og:title", document.title],
  ["property", "og:description", shareDescription],
  ["property", "og:url", publicSite],
  ["property", "og:image", shareImage],
  ["property", "og:image:type", "image/png"],
  ["property", "og:image:width", "1200"],
  ["property", "og:image:height", "630"],
  ["property", "og:image:alt", "ClippedUp logo. Your videos, clipped up."],
  ["name", "twitter:card", "summary_large_image"],
  ["name", "twitter:title", document.title],
  ["name", "twitter:description", shareDescription],
  ["name", "twitter:image", shareImage],
  ["name", "twitter:image:alt", "ClippedUp logo. Your videos, clipped up."],
]) {
  const meta = document.querySelector(`meta[${attribute}="${name}"]`) || document.createElement("meta");
  meta.setAttribute(attribute, name);
  meta.content = content;
  if (!meta.isConnected) document.head.appendChild(meta);
}

const brandIcon = document.querySelector('link[rel="icon"]') || document.createElement("link");
brandIcon.rel = "icon";
brandIcon.type = "image/svg+xml";
brandIcon.href = `${process.env.PUBLIC_URL}/clippedup-mark.svg?v=2`;
brandIcon.setAttribute("sizes", "any");
brandIcon.setAttribute("data-testid", "brand-favicon");
if (!brandIcon.isConnected) document.head.appendChild(brandIcon);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} storageKey="cg_theme" themes={["light", "dark"]} disableTransitionOnChange>
        <App />
      </ThemeProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
